import { QueryFunctionContext, useInfiniteQuery, useQuery } from "@tanstack/react-query"

import {
    getSelections,
    getCalendar,
    getMostImportantBookings,
    getPastBookings,
    getFutureBookings,
    getPaymentMethods,
    getRefShareLink,
    getCanceledBookings,
    Booking,
    getTelegramStartUrl,
} from "modules/ClientAccount/utils"

const FUTURE_BOOKINGS_KEY = "FUTURE_BOOKINGS_KEY"
const PAST_BOOKINGS_WITH_PAGINATION_KEY = "PAST_BOOKINGS_WITH_PAGINATION_KEY"
const CANCELED_BOOKINGS_WITH_PAGES_KEY = "CANCELED_BOOKINGS_WITH_PAGES_KEY"
const CANCELED_BOOKINGS_FIRST_PAGE_KEY = "CANCELED_BOOKINGS_FIRST_PAGE_KEY"
const MOST_IMPORTANT_BOOKINGS_KEY = "MOST_IMPORTANT_BOOKINGS_KEY"
const SELECTIONS_KEY = "SELECTIONS_KEY"
const CLIENT_CALENDAR_LINK_KEY = "CLIENT_CALENDAR_LINK_KEY"
const CLIENT_PAYMENT_METHODS_KEY = "CLIENT_PAYMENT_METHODS_KEY"
const CLIENT_REF_LINK_KEY = "CLIENT_REF_LINK_KEY"
const CLIENT_TELEGRAM_START_URL = "CLIENT_TELEGRAM_START_URL"

function sortBookingByStartDate(booking: Booking[]) {
    return booking.sort((a, b) => (a?.session_start_datetime > b?.session_start_datetime ? -1 : 1))
}

export function useGetFutureBookings() {
    return useQuery([FUTURE_BOOKINGS_KEY], getFutureBookings, {
        refetchOnMount: false,
    })
}

export function useGetHasFutureBookings() {
    return useQuery([FUTURE_BOOKINGS_KEY], getFutureBookings, {
        select: (data) => data.length > 0,
        refetchOnMount: false,
    })
}

export function useHasFutureFixedTime() {
    return useQuery([FUTURE_BOOKINGS_KEY], getFutureBookings, {
        select: (data) => data.filter((item) => item.has_fixed_session_time).length > 0,
        refetchOnMount: false,
    })
}

export function useGetPastBookingsWithPagination() {
    const props = useInfiniteQuery(
        [PAST_BOOKINGS_WITH_PAGINATION_KEY],
        ({ pageParam }: QueryFunctionContext<["PAST_BOOKINGS_WITH_PAGINATION_KEY"], number>) =>
            getPastBookings(pageParam),
        {
            getNextPageParam: (lastPage) => lastPage.next,
            refetchOnMount: false,
        }
    )
    const pageResults = props.data?.pages[0]?.results
    return {
        ...props,
        hasPastBooking: !!pageResults?.[0],
        newestPastBooking: pageResults?.[0],
        pastBookingFirstPage: pageResults,
    }
}

export function useGetCanceledBookingsWithPages() {
    const props = useInfiniteQuery(
        [CANCELED_BOOKINGS_WITH_PAGES_KEY],
        ({ pageParam }: QueryFunctionContext<["CANCELED_BOOKINGS_WITH_PAGES_KEY"], number>) =>
            getCanceledBookings(pageParam),
        {
            getNextPageParam: (lastPage) => lastPage.next,
            refetchOnMount: false,
        }
    )
    const pageResults = props.data?.pages[0]?.results
    return {
        ...props,
        hasCanceledBooking: !!pageResults?.[0],
        newestCanceledBooking: pageResults && sortBookingByStartDate(pageResults)[0],
    }
}

export function useHasCanceledBooking() {
    return useQuery([CANCELED_BOOKINGS_FIRST_PAGE_KEY], () => getCanceledBookings(1), {
        select: (data) => data.results.length > 0,
        refetchOnMount: false,
    })
}

export function useGetSelections() {
    return useQuery([SELECTIONS_KEY], getSelections)
}

export function useGetHasSelections() {
    return useQuery([SELECTIONS_KEY], getSelections, {
        select: (data) => data.length > 0,
    })
}

export function useGetClientCalendarLink() {
    return useQuery([CLIENT_CALENDAR_LINK_KEY], getCalendar, {
        refetchOnWindowFocus: false,
    })
}

export function useGetMostImportantBookings(enabled = true) {
    return useQuery([MOST_IMPORTANT_BOOKINGS_KEY], getMostImportantBookings, {
        refetchOnMount: false,
        enabled,
    })
}

export function useGetClientPaymentMethods() {
    return useQuery([CLIENT_PAYMENT_METHODS_KEY], getPaymentMethods)
}

export function useGetRefShareLink() {
    return useQuery([CLIENT_REF_LINK_KEY], getRefShareLink, {
        refetchOnWindowFocus: false,
    })
}

export function useGetTelegramStartUrl() {
    return useQuery([CLIENT_TELEGRAM_START_URL], getTelegramStartUrl, {
        refetchOnWindowFocus: false,
        enabled: false,
        retry: false,
        cacheTime: 0,
    })
}
